import { Control, type ControlOptions } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs, WheelEventArgs } from "../core/events.js";
import { clamp, type Rect, type Size } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { SliderParts, Theme } from "../themes/Theme.js";
import type { Orientation } from "./ScrollBar.js";

export interface SliderOptions extends ControlOptions {
  orientation?: Orientation;
  minimum?: number;
  maximum?: number;
  value?: number;
  /**
   * Round the value to a multiple of this. 0 leaves it continuous, which is
   * what a volume or gamma control wants; 1 makes it an integer picker.
   */
  step?: number;
  /** Arrow-key and wheel change. Defaults to `step`, or a hundredth of the range. */
  smallChange?: number;
  /** PageUp / PageDown change. Defaults to ten small changes. */
  largeChange?: number;
  /** Draw a tick every this many units. 0 for none. */
  tickFrequency?: number;
  onChange?: (value: number) => void;
}

/**
 * A value picked by sliding a thumb along a track — WinForms' TrackBar.
 *
 * Not a `ScrollBar`: a scroll bar's value is the leading edge of a viewport
 * and its range shrinks by that viewport, whereas a slider's value reaches
 * both ends of its range. The two look similar and behave nothing alike.
 *
 * Pressing anywhere on the track jumps the thumb there and starts dragging,
 * rather than paging towards it the way Win95 did. Every settings screen
 * written this century behaves that way, and a game's audio slider being
 * draggable from wherever it was clicked is worth more than the fidelity.
 */
export class Slider extends Control {
  #orientation: Orientation;
  #minimum: number;
  #maximum: number;
  #value: number;
  #step: number;
  #smallChange: number | null;
  #largeChange: number | null;
  #dragging = false;

  /** Draw a tick every this many units; 0 for none. */
  tickFrequency: number;

  constructor(options: SliderOptions = {}) {
    super({ width: 160, height: 28, tabStop: true, ...options });
    this.#orientation = options.orientation ?? "horizontal";
    this.#minimum = options.minimum ?? 0;
    this.#maximum = options.maximum ?? 100;
    if (this.#maximum < this.#minimum) this.#maximum = this.#minimum;
    this.#step = Math.max(0, options.step ?? 0);
    this.#smallChange = options.smallChange ?? null;
    this.#largeChange = options.largeChange ?? null;
    this.tickFrequency = Math.max(0, options.tickFrequency ?? 0);
    this.#value = this.#coerce(options.value ?? this.#minimum);
    if (options.onChange) this.on("change", () => options.onChange!(this.#value));

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#endDrag());
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("wheel", (e) => this.#onWheel(e));
  }

  // --- value ---------------------------------------------------------------

  get value(): number {
    return this.#value;
  }
  set value(v: number) {
    const next = this.#coerce(v);
    if (next === this.#value) return;
    this.#value = next;
    this.emit("valuechanged");
    this.emit("change");
    this.invalidate();
  }

  get minimum(): number {
    return this.#minimum;
  }
  set minimum(v: number) {
    this.setRange(v, this.#maximum);
  }

  get maximum(): number {
    return this.#maximum;
  }
  set maximum(v: number) {
    this.setRange(this.#minimum, v);
  }

  /** Set both ends at once; keeps `value` inside them. */
  setRange(minimum: number, maximum: number): void {
    const lo = Number.isFinite(minimum) ? minimum : 0;
    const hi = Math.max(lo, Number.isFinite(maximum) ? maximum : lo);
    if (lo === this.#minimum && hi === this.#maximum) return;
    this.#minimum = lo;
    this.#maximum = hi;
    this.value = this.#value;
    this.invalidate();
  }

  get step(): number {
    return this.#step;
  }
  set step(v: number) {
    this.#step = Math.max(0, Number.isFinite(v) ? v : 0);
    this.value = this.#value;
  }

  /** How far one arrow key or wheel notch moves the value. */
  get smallChange(): number {
    if (this.#smallChange !== null) return this.#smallChange;
    if (this.#step > 0) return this.#step;
    return (this.#maximum - this.#minimum) / 100 || 1;
  }
  set smallChange(v: number) {
    this.#smallChange = v;
  }

  get largeChange(): number {
    return this.#largeChange ?? this.smallChange * 10;
  }
  set largeChange(v: number) {
    this.#largeChange = v;
  }

  /** Position within the range, 0..1. */
  get fraction(): number {
    const span = this.#maximum - this.#minimum;
    return span > 0 ? (this.#value - this.#minimum) / span : 0;
  }

  get orientation(): Orientation {
    return this.#orientation;
  }
  set orientation(v: Orientation) {
    if (this.#orientation === v) return;
    this.#orientation = v;
    this.invalidate();
  }

  get vertical(): boolean {
    return this.#orientation === "vertical";
  }

  /** Clamp to the range, then snap to `step`. */
  #coerce(v: number): number {
    if (!Number.isFinite(v)) return this.#value ?? this.#minimum;
    let next = clamp(v, this.#minimum, this.#maximum);
    if (this.#step > 0) {
      next = this.#minimum + Math.round((next - this.#minimum) / this.#step) * this.#step;
      next = clamp(next, this.#minimum, this.#maximum);
      // Snapping in floating point leaves 0.30000000000000004 behind; round
      // to the step's own precision so a caller reading `value` sees 0.3.
      const decimals = decimalsOf(this.#step);
      if (decimals > 0) next = Number(next.toFixed(decimals));
    }
    return next;
  }

  // --- geometry ------------------------------------------------------------

  /**
   * Where the track, the filled part and the thumb sit, for the theme to
   * paint and for hit-testing to measure against.
   */
  parts(theme: Theme): SliderParts {
    const m = theme.metrics;
    const size = m.sliderThumbSize;
    const thickness = m.sliderTrackThickness;
    const bounds = this.localRect;
    const vertical = this.vertical;
    // Ticks sit outside the track, so the track is offset towards the near
    // edge to leave room for them rather than centered on nothing.
    const room = this.tickFrequency > 0 ? 10 : 0;

    const track: Rect = vertical
      ? {
          x: (bounds.width - room - thickness) / 2,
          y: size / 2,
          width: thickness,
          height: Math.max(0, bounds.height - size),
        }
      : {
          x: size / 2,
          y: (bounds.height - room - thickness) / 2,
          width: Math.max(0, bounds.width - size),
          height: thickness,
        };

    // Vertical sliders fill from the bottom: up is more, as on a mixing desk.
    const travel = vertical ? track.height : track.width;
    const along = vertical
      ? track.y + track.height - this.fraction * travel
      : track.x + this.fraction * travel;

    const thumb: Rect = vertical
      ? { x: track.x + thickness / 2 - size / 2, y: along - size / 2, width: size, height: size }
      : { x: along - size / 2, y: track.y + thickness / 2 - size / 2, width: size, height: size };

    const filled: Rect = vertical
      ? { x: track.x, y: along, width: track.width, height: track.y + track.height - along }
      : { x: track.x, y: track.y, width: along - track.x, height: track.height };

    return { bounds, track, filled, thumb, vertical, ticks: this.#ticks(track) };
  }

  #ticks(track: Rect): number[] {
    const span = this.#maximum - this.#minimum;
    if (this.tickFrequency <= 0 || span <= 0) return [];
    const travel = this.vertical ? track.height : track.width;
    const start = this.vertical ? track.y + track.height : track.x;
    const marks: number[] = [];
    // Bounded so a tickFrequency of 0.001 over a range of 1000 cannot try to
    // draw a million marks and take the frame with it.
    const count = Math.min(Math.floor(span / this.tickFrequency), 200);
    for (let i = 0; i <= count; i++) {
      const offset = ((i * this.tickFrequency) / span) * travel;
      marks.push(this.vertical ? start - offset : start + offset);
    }
    return marks;
  }

  /** The value the pointer is pointing at, in local coordinates. */
  #valueAt(x: number, y: number, theme: Theme): number {
    const { track } = this.parts(theme);
    const travel = this.vertical ? track.height : track.width;
    if (travel <= 0) return this.#minimum;
    const offset = this.vertical ? track.y + track.height - y : x - track.x;
    const fraction = clamp(offset / travel, 0, 1);
    return this.#minimum + fraction * (this.#maximum - this.#minimum);
  }

  // --- interaction ---------------------------------------------------------

  /** The whole control is the drag, so a pan must not steal it. */
  override handlesDragAt(): boolean {
    return true;
  }

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0) return;
    const theme = this.desktop?.theme;
    if (!theme) return;
    this.focus();
    this.#dragging = true;
    this.desktop?.input.setCapture(this);
    this.value = this.#valueAt(e.x, e.y, theme);
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#dragging) return;
    const theme = this.desktop?.theme;
    if (theme) this.value = this.#valueAt(e.x, e.y, theme);
  }

  #endDrag(): void {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.desktop?.input.releaseCapture(this);
  }

  #onKeyDown(e: KeyEventArgs): void {
    // Left and down decrease whichever way the slider runs: on a vertical
    // one down is towards the low end, and on a horizontal one left is.
    const step = (delta: number): void => {
      this.value = this.#value + delta;
      e.handled = true;
    };
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        step(-this.smallChange);
        break;
      case "ArrowRight":
      case "ArrowUp":
        step(this.smallChange);
        break;
      case "PageDown":
        step(-this.largeChange);
        break;
      case "PageUp":
        step(this.largeChange);
        break;
      case "Home":
        this.value = this.#minimum;
        e.handled = true;
        break;
      case "End":
        this.value = this.#maximum;
        e.handled = true;
        break;
      default:
        break;
    }
  }

  #onWheel(e: WheelEventArgs): void {
    if (e.deltaY === 0) return;
    this.value = this.#value - Math.sign(e.deltaY) * this.smallChange;
    e.handled = true;
  }


  /** Keeps the thickness it was given and takes whatever length it is offered. */
  override preferredSize(available: Size): Size {
    return this.vertical
      ? { width: this.width, height: available.height }
      : { width: available.width, height: this.height };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    theme.drawSlider(rc, this.parts(theme), {
      ...this.visualState(),
      pressed: this.#dragging,
    });
  }
}

/** How many decimal places `step` carries, so snapping can round to them. */
function decimalsOf(step: number): number {
  const text = String(step);
  const dot = text.indexOf(".");
  if (dot < 0 || text.includes("e")) return 0;
  return Math.min(text.length - dot - 1, 10);
}
